import { createSlice } from '@reduxjs/toolkit'
import { StoreStatus } from 'common/utils/types'
import { ProposalStatus } from 'common/tenders/tenders.types'

import {
  getBidList,
  fetchTenderProject,
  submitProposal,
  upsertProposal,
  getProposalInfo,
  getTenderById,
  bidAnswer,
} from './bid.service'
import { type Bid, type TenderProject, type ProposalTender, type Proposal } from './bid.types'

export interface BidState {
  status: StoreStatus
  bidList: Bid[]
  tenderProject: TenderProject | null
  getTenderProjectStatus: StoreStatus
  count: number
  getBidListStatus: StoreStatus
  proposal: Proposal | null
  upsertProposalStatus: StoreStatus
  getProposalInfoStatus: StoreStatus
  submitProposalStatus: StoreStatus
  proposalTender: ProposalTender | null
  proposalTenderStatus: StoreStatus
  bidAnswerStatus: StoreStatus
}

const initialState: BidState = {
  status: StoreStatus.Idle,
  bidList: [],
  count: 0,
  getBidListStatus: StoreStatus.Idle,
  tenderProject: null,
  getTenderProjectStatus: StoreStatus.Idle,
  upsertProposalStatus: StoreStatus.Idle,
  proposal: null,
  getProposalInfoStatus: StoreStatus.Idle,
  submitProposalStatus: StoreStatus.Idle,
  proposalTender: null,
  proposalTenderStatus: StoreStatus.Idle,
  bidAnswerStatus: StoreStatus.Idle,
}

export const BidSlice = createSlice({
  name: 'vendor',
  initialState,
  reducers: {
    resetState: (state) => ({
      ...initialState,
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(getBidList.pending, (state, { payload }) => ({
      ...state,
      getBidListStatus: StoreStatus.Loading,
    }))
    builder.addCase(getBidList.fulfilled, (state, { payload }) => ({
      ...state,
      bidList: payload.page === 0 ? payload.list.data : [...state.bidList, ...payload.list.data],
      count: payload.list.count,
      getBidListStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getBidList.rejected, (state) => ({
      ...state,
      bidList: [],
      count: 0,
      getBidListStatus: StoreStatus.Failed,
    }))
    builder.addCase(upsertProposal.pending, (state) => ({
      ...state,
      upsertProposalStatus: StoreStatus.Loading,
    }))
    builder.addCase(upsertProposal.fulfilled, (state, { payload }) => ({
      ...state,
      upsertProposalStatus: StoreStatus.Succeeded,
      proposal: payload,
    }))
    builder.addCase(upsertProposal.rejected, (state) => ({
      ...state,
      upsertProposalStatus: StoreStatus.Failed,
    }))
    builder.addCase(submitProposal.pending, (state) => ({
      ...state,
      submitProposalStatus: StoreStatus.Loading,
    }))
    builder.addCase(submitProposal.fulfilled, (state, { payload }) => {
      state.submitProposalStatus = StoreStatus.Succeeded
      if (state.proposal?.status) {
        state.proposal.status = ProposalStatus.SUBMITTED
      }
    })
    builder.addCase(submitProposal.rejected, (state) => ({
      ...state,
      submitProposalStatus: StoreStatus.Failed,
    }))
    builder.addCase(getProposalInfo.pending, (state) => ({
      ...state,
      getProposalInfoStatus: StoreStatus.Loading,
    }))
    builder.addCase(getProposalInfo.fulfilled, (state, { payload }) => ({
      ...state,
      getProposalInfoStatus: StoreStatus.Succeeded,
      proposal: payload,
    }))
    builder.addCase(getProposalInfo.rejected, (state) => ({
      ...state,
      getProposalInfoStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(getTenderById.pending, (state) => ({
      ...state,
      proposalTenderStatus: StoreStatus.Loading,
    }))
    builder.addCase(getTenderById.fulfilled, (state, { payload }) => ({
      ...state,
      proposalTenderStatus: StoreStatus.Succeeded,
      proposalTender: payload,
    }))
    builder.addCase(getTenderById.rejected, (state) => ({
      ...state,
      proposalTenderStatus: StoreStatus.Failed,
    }))
    builder.addCase(fetchTenderProject.pending, (state, { payload }) => ({
      ...state,
      getTenderProjectStatus: StoreStatus.Loading,
    }))
    builder.addCase(fetchTenderProject.fulfilled, (state, { payload }) => ({
      ...state,
      tenderProject: payload,
      getTenderProjectStatus: StoreStatus.Succeeded,
    }))
    builder.addCase(fetchTenderProject.rejected, (state) => ({
      ...state,
      tenderProject: null,
      getTenderProjectStatus: StoreStatus.Failed,
    }))
    builder.addCase(bidAnswer.pending, (state, { payload }) => ({
      ...state,
      bidAnswerStatus: StoreStatus.Loading,
    }))
    builder.addCase(bidAnswer.fulfilled, (state, { payload, meta }) => {
      const projectId = meta.arg.projectId
      state.bidList = state.bidList.filter((bid) => bid.id !== projectId)
      state.bidAnswerStatus = StoreStatus.Succeeded
    })
    builder.addCase(bidAnswer.rejected, (state, { payload }) => ({
      ...state,
      bidAnswerStatus: StoreStatus.Failed,
    }))
  },
})

export default BidSlice.reducer
